/**
 * registerErrors.ts — map register/login failures to actionable, PHI-free copy.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY: the gateway/identity boundary returns many failure shapes (Zod field
 * errors, Nest `{message}`, age-gate 403, conflict 409, 404 when a downstream
 * service URL is unset, 502 when identity is down). Collapsing every non-field
 * failure into "Please try again" hides the real reason from patients and
 * developers. This module is the single mapping for auth forms.
 */
import { ApiError } from '../api/client';
import { isValidEmail } from '../lib/email';

export { isValidEmail };

/** Password length bounds matching gpsclinics-contracts `password`. */
export const MIN_PASSWORD = 10;
export const MAX_PASSWORD = 200;

export interface MappedAuthError {
  /** Form-level alert (role="alert"). */
  formError?: string;
  /** Field-level hints keyed by wire field name. */
  fieldErrors?: Record<string, string>;
}

function firstFieldError(error: ApiError, field: string): string | undefined {
  return error.fields?.fieldErrors?.[field]?.[0];
}

function networkFailure(err: unknown): boolean {
  return (
    err instanceof TypeError ||
    (err instanceof Error && /failed to fetch|networkerror|load failed/i.test(err.message))
  );
}

/**
 * Map a thrown register failure into user-facing copy.
 * Never returns stack traces or raw response bodies beyond Nest's PHI-free
 * `message` / Zod fieldErrors already parsed into ApiError.
 */
export function mapRegisterError(err: unknown): MappedAuthError {
  if (networkFailure(err)) {
    return {
      formError: 'Unable to reach the server. Please check your connection and try again.',
    };
  }

  if (!(err instanceof ApiError)) {
    console.error('Unexpected register failure', err);
    return {
      formError: "We couldn't create your account right now. Please try again in a few minutes.",
    };
  }

  const fieldErrors: Record<string, string> = {};
  for (const field of ['email', 'password', 'dateOfBirth', 'assessmentId'] as const) {
    const msg = firstFieldError(err, field);
    if (msg) {
      // Soften Zod's terse messages for patients.
      if (field === 'password' && /at least|min/i.test(msg)) {
        fieldErrors.password = `Password must contain at least ${MIN_PASSWORD} characters.`;
      } else if (field === 'password' && /at most|max/i.test(msg)) {
        fieldErrors.password = `Password must be at most ${MAX_PASSWORD} characters.`;
      } else if (field === 'email') {
        fieldErrors.email = 'Enter a valid email address.';
      } else {
        fieldErrors[field] = msg;
      }
    }
  }
  if (Object.keys(fieldErrors).length > 0) return { fieldErrors };

  if (err.status === 409) {
    return {
      formError: 'This email address already has an account. Please sign in instead.',
    };
  }

  if (err.status === 403) {
    const detail = err.detail?.trim();
    return {
      formError:
        detail && detail.length < 200
          ? detail
          : 'This service is only available to adults aged 18 and over.',
    };
  }

  // Gateway stub mode: IDENTITY_SERVICE_URL unset → "Unknown service: identity".
  // Downstream down → 502. Both are local/ops failures, not patient mistakes.
  if (err.status === 404 || err.status === 502 || err.status === 503) {
    console.error(`Register unavailable (${err.status})`, err.detail ?? err.message);
    return {
      formError:
        'Account creation is temporarily unavailable. Please try again in a few minutes. If this keeps happening, contact support.',
    };
  }

  if (err.status === 400 || err.status === 422) {
    if (err.detail && err.detail.length < 200 && !/stack|exception/i.test(err.detail)) {
      return { formError: err.detail };
    }
    return { formError: 'Please check your details and try again.' };
  }

  console.error(`Register failed (${err.status})`, err.detail ?? err.message);
  return {
    formError: "We couldn't create your account right now. Please try again in a few minutes.",
  };
}

/** Map a thrown login failure into user-facing copy. */
export function mapLoginError(err: unknown): MappedAuthError {
  if (networkFailure(err)) {
    return {
      formError: 'Unable to reach the server. Please check your connection and try again.',
    };
  }
  if (err instanceof ApiError && err.status === 401) {
    return { formError: 'That email or password was not recognised.' };
  }
  if (err instanceof ApiError && (err.status === 404 || err.status === 502 || err.status === 503)) {
    console.error(`Login unavailable (${err.status})`, err.detail ?? err.message);
    return {
      formError: 'Sign-in is temporarily unavailable. Please try again in a few minutes.',
    };
  }
  console.error('Unexpected login failure', err);
  return { formError: "We couldn't sign you in right now. Please try again in a few minutes." };
}
